import React, { useEffect, useMemo, useState } from "react";
import Box from "@amzn/meridian/box";
import Button from "@amzn/meridian/button";
import Alert from "@amzn/meridian/alert";
import Text from "@amzn/meridian/text";
import Row from "@amzn/meridian/row";
import Column from "@amzn/meridian/column";
import Heading from "@amzn/meridian/heading";
import Modal, { ModalFooter } from "@amzn/meridian/modal";
import DataTable, {
  DataTableBody,
  DataTableHeader,
  DataTableCell,
  DataTableRow,
  DataTableHead,
} from "@amzn/meridian/data-table";
import Tag from "@amzn/meridian/tag";

import { useAppDispatch, useAppSelector } from "../../../store";
import { selectStationCode } from "../../../store/slices/shiftPlanSlice";
import { showError, showNotification } from "../../../store/slices/errorSlice";
import {
  fetchCertificateRules,
  saveCertificateRule,
  deleteCertificateRule,
  selectCertificateRulesState,
} from "../../../store/slices/certificateRulesSlice";
import { getUserName } from "../../../services/boson/bosonClient";
import { isLocalEnvironment } from "../../../utils/environmentDetection";
import CertificateRuleModal, { functionDisplayKey, functionKey } from "./certificateRules/CertificateRuleModal";
import { parseExpression, treeToChain } from "./certificateRules/transform";
import { CertificateFunctionMapping, ExpressionNode } from "./certificateRules/types";

const TABLE_COLUMNS = ["Process ID - Function ID", "Certificate IDs & Logic", "Actions"];

const CONFLICT_MESSAGE_FRAGMENT = "changed by someone else";

const LOCAL_DEV_USER = "local-dev@amazon.com";

/** Resolves the operator alias. Boson isn't available on a local dev server, so fall back to a fixed alias there. */
const resolveRequestedBy = async (): Promise<string> => {
  try {
    return await getUserName();
  } catch (err) {
    if (isLocalEnvironment()) return LOCAL_DEV_USER;
    throw err;
  }
};

/** Chips for each certificate with an AND/OR tag between them. */
const CertificateChain: React.FC<{ expression: string }> = ({ expression }) => {
  const tree = parseExpression(expression);
  if (!tree) return <Text color="secondary">Unreadable rule</Text>;
  const { certs, connectors } = treeToChain(tree);
  return (
    <Row spacing="100" wrap="down">
      {certs.map((cert, i) => (
        <React.Fragment key={`${cert}-${i}`}>
          <Tag type="theme">{cert}</Tag>
          {i < certs.length - 1 && (
            <Tag type={connectors[i] === "AND" ? "warning" : "success"}>{connectors[i] ?? ""}</Tag>
          )}
        </React.Fragment>
      ))}
    </Row>
  );
};

const CertificateRulesTab: React.FC = () => {
  const dispatch = useAppDispatch();
  const stationCode = useAppSelector(selectStationCode);
  const { mappings, availableFunctions, status, saving, error } = useAppSelector(selectCertificateRulesState);

  const [modalOpen, setModalOpen] = useState(false);
  const [editMapping, setEditMapping] = useState<CertificateFunctionMapping | null>(null);
  const [serverError, setServerError] = useState<string | undefined>(undefined);
  const [pendingDelete, setPendingDelete] = useState<CertificateFunctionMapping | null>(null);

  useEffect(() => {
    if (stationCode) {
      dispatch(fetchCertificateRules(stationCode));
    }
  }, [dispatch, stationCode]);

  const mappedKeys = useMemo(() => new Set(mappings.map(functionKey)), [mappings]);

  const openAdd = () => {
    setEditMapping(null);
    setServerError(undefined);
    setModalOpen(true);
  };

  const openEdit = (mapping: CertificateFunctionMapping) => {
    setEditMapping(mapping);
    setServerError(undefined);
    setModalOpen(true);
  };

  const handleFailure = (message: string) => {
    if (stationCode && message.includes(CONFLICT_MESSAGE_FRAGMENT)) {
      dispatch(fetchCertificateRules(stationCode));
    }
  };

  const handleSave = async (
    processId: string,
    functionId: string,
    expression: ExpressionNode,
    version?: number
  ) => {
    if (!stationCode) return;
    let requestedBy: string;
    try {
      requestedBy = await resolveRequestedBy();
    } catch (err: any) {
      setServerError(err?.message ?? "Unable to determine current user");
      return;
    }
    const result = await dispatch(
      saveCertificateRule({ warehouseId: stationCode, requestedBy, processId, functionId, expression, version })
    );
    if (saveCertificateRule.fulfilled.match(result)) {
      setModalOpen(false);
      dispatch(showNotification({ message: "Mapping saved successfully", type: "success" }));
    } else {
      const message = result.payload || "Failed to save certificate rule";
      setServerError(message);
      handleFailure(message);
    }
  };

  const handleConfirmDelete = async () => {
    if (!stationCode || !pendingDelete) return;
    const mapping = pendingDelete;
    setPendingDelete(null);
    let requestedBy: string;
    try {
      requestedBy = await resolveRequestedBy();
    } catch (err: any) {
      dispatch(showError({ message: err?.message ?? "Unable to determine current user" }));
      return;
    }
    const result = await dispatch(
      deleteCertificateRule({
        warehouseId: stationCode,
        requestedBy,
        processId: mapping.processId,
        functionId: mapping.functionId,
        version: mapping.version,
      })
    );
    if (deleteCertificateRule.fulfilled.match(result)) {
      dispatch(showNotification({ message: "Mapping deleted successfully", type: "success" }));
    } else {
      const message = result.payload || "Failed to delete certificate rule";
      dispatch(showError({ message }));
      handleFailure(message);
    }
  };

  if (!stationCode) {
    return (
      <Box spacingInset="400">
        <Alert type="neutral">Please select a warehouse to view certificate rules.</Alert>
      </Box>
    );
  }

  return (
    <Box spacingInset="400">
      <Row alignmentHorizontal="justify" alignmentVertical="top">
        <Column spacing="100">
          <Heading level={3} type="h300">
            Certificate ID → Function ID Mapping
          </Heading>
          <Text color="secondary">
            Map Umbrella certificate IDs to FCLM function IDs so Station Assignment knows path eligibility.
            Scoped to your site.
          </Text>
        </Column>
        <Button type="primary" onClick={openAdd} disabled={status === "loading"}>
          + Add Mapping
        </Button>
      </Row>

      {error && (
        <Box spacingInset="300 none">
          <Alert type="error">{error}</Alert>
        </Box>
      )}

      {status === "loading" ? (
        <Box spacingInset="500">
          <Text color="secondary">Loading certificate rules...</Text>
        </Box>
      ) : (
        <Box spacingInset="300 none">
          <DataTable showRowDividers>
            <DataTableHead>
              <DataTableRow>
                {TABLE_COLUMNS.map((col) => (
                  <DataTableHeader key={col}>{col}</DataTableHeader>
                ))}
              </DataTableRow>
            </DataTableHead>
            <DataTableBody>
              {mappings.length === 0 ? (
                <DataTableRow>
                  <DataTableCell colSpan={TABLE_COLUMNS.length}>
                    <Box spacingInset="400">
                      <Text type="b300">No mappings yet</Text>
                      <Text color="secondary">
                        Add your first Function ID → Certificate ID mapping to get started.
                      </Text>
                    </Box>
                  </DataTableCell>
                </DataTableRow>
              ) : (
                mappings.map((m) => (
                  <DataTableRow key={functionKey(m)}>
                    <DataTableCell>
                      <Text type="h100">{functionDisplayKey(m)}</Text>
                      <Text type="b200" color="secondary">
                        {m.functionName}
                      </Text>
                    </DataTableCell>
                    <DataTableCell>
                      <CertificateChain expression={m.expression} />
                    </DataTableCell>
                    <DataTableCell>
                      <Row spacing="200">
                        <Button type="tertiary" size="small" onClick={() => openEdit(m)} disabled={saving}>
                          Edit
                        </Button>
                        <Button type="tertiary" size="small" onClick={() => setPendingDelete(m)} disabled={saving}>
                          Delete
                        </Button>
                      </Row>
                    </DataTableCell>
                  </DataTableRow>
                ))
              )}
            </DataTableBody>
          </DataTable>
        </Box>
      )}

      <CertificateRuleModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleSave}
        editMapping={editMapping}
        availableFunctions={availableFunctions}
        mappedKeys={mappedKeys}
        isSaving={saving}
        serverError={serverError}
      />

      <Modal
        open={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        title="Delete this mapping?"
        scrollContainer="viewport"
        width="400px"
      >
        <Box spacingInset="400">
          <Text>This will remove the Function ID mapping and cannot be undone.</Text>
        </Box>
        <ModalFooter>
          <Row alignmentHorizontal="end" spacing="300">
            <Button type="tertiary" onClick={() => setPendingDelete(null)}>
              Cancel
            </Button>
            <Button type="primary" onClick={handleConfirmDelete}>
              Delete
            </Button>
          </Row>
        </ModalFooter>
      </Modal>
    </Box>
  );
};

export default CertificateRulesTab;
